import { isAddedLessThan2Minutes } from "./index";

const ANSWERED_KEY_PREFIX = "hw_question_form_answered_";

/**
 * Splits the single "full name" field into the account's two name fields.
 * The last word is the last name; everything before it (middle names included)
 * is the first name. A single word is a first name only, so name_l is omitted
 * and the stored last name is left untouched.
 *
 * @param {string} fullName
 * @returns {{name_f: string, name_l?: string}}
 */
export const splitFullName = fullName => {
  const words = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length < 2) {
    return { name_f: words[0] || "" };
  }

  return { name_f: words.slice(0, -1).join(" "), name_l: words[words.length - 1] };
};

/**
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string}
 */
export const joinFullName = (firstName, lastName) =>
  [firstName, lastName]
    .map(part => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

/**
 * Birthdate as the account stores it (yyyy-MM-dd, same as My Account). The calendar
 * emits MM/DD/YYYY; incomplete or impossible dates give "" so they are not saved.
 *
 * @param {string} value
 * @returns {string}
 */
export const formatBirthDate = value => {
  const text = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!match) {
    return "";
  }

  const [, month, day, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const isRealDate = date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day);

  return isRealDate ? `${year}-${month}-${day}` : "";
};

/**
 * Initial values of the "few questions" pop-up, taken from the account.
 *
 * @param {object} profile
 * @returns {{name: string, birth_date: string, country: string, state: string, city: string, zip: string}}
 */
export const getQuestionFormInitialValues = (profile = {}) => ({
  name: joinFullName(profile.name_f, profile.name_l),
  birth_date: profile.birth_date || "",
  country: profile.country || "",
  state: profile.state || "",
  city: profile.city || "",
  zip: profile.zip || ""
});

/**
 * The subset of a /my-account/contacts payload the backend actually stores:
 * name and birthdate only when non-empty, location fields whenever they are sent.
 * Mirrors MyAccountController::updateContacts().
 *
 * @param {object} data
 * @returns {object}
 */
export const getSavedContactFields = (data = {}) => {
  const saved = {};

  ["name_f", "name_l", "birth_date"].forEach(field => {
    if (data[field]) {
      saved[field] = data[field];
    }
  });

  ["country", "state", "city", "zip"].forEach(field => {
    if (data[field] !== undefined) {
      saved[field] = String(data[field] ?? "").trim();
    }
  });

  return saved;
};

// localStorage can throw (private mode, blocked site data); the marker is best effort.
export const isQuestionFormAnswered = userId => {
  if (!userId) {
    return false;
  }

  try {
    return window.localStorage.getItem(`${ANSWERED_KEY_PREFIX}${userId}`) === "1";
  } catch {
    return false;
  }
};

export const markQuestionFormAnswered = userId => {
  if (!userId) {
    return;
  }

  try {
    window.localStorage.setItem(`${ANSWERED_KEY_PREFIX}${userId}`, "1");
  } catch {
    // ignore
  }
};

/**
 * The pop-up belongs to the first dashboard visit: the email was verified within
 * the last two minutes, the account has no birthdate yet, and this user has not
 * already answered it in this browser.
 *
 * @param {object} profile
 * @param {number} now epoch milliseconds, injectable for tests
 * @returns {boolean}
 */
export const shouldShowQuestionForm = (profile = {}, now = Date.now()) =>
  Boolean(profile.user_id) &&
  !profile.birth_date &&
  !isQuestionFormAnswered(profile.user_id) &&
  isAddedLessThan2Minutes(profile.email_verified_at, now);
